/**
 * PvE Socket.IO Client
 *
 * Client-side Socket.IO connection for PvE (Weekly Challenge) battles.
 * Connects to the /pve namespace for real-time PvE combat.
 */

import { io, Socket } from 'socket.io-client';
import { getAuth } from 'firebase/auth';
import type { CombatState, CombatEvent } from '@drawn-of-war/shared';
import { StateDeltaDecoder } from './state-delta-decoder';
import type { CombatBroadcast } from './state-delta-decoder';

// =====================================================================
// TYPES
// =====================================================================

interface PvEStatePayload {
  matchId: string;
  phase: string;
  enemies: Array<{
    instanceId: string;
    name: string;
    position: { q: number; r: number };
    health: number;
    difficultyTier: string;
    isBoss: boolean;
  }>;
  player: {
    isReady: boolean;
    creatures: Array<{
      creatureId: string;
      name: string;
      position?: { q: number; r: number };
    }>;
  };
}

interface PvEResultPayload {
  matchId: string;
  outcome: 'victory' | 'defeat' | 'timeout';
  reason: string;
  stats: {
    damageDealt: number;
    damageTaken: number;
    enemiesKilled: number;
    creatureLosses: number;
    ticksElapsed: number;
  };
  stars?: 0 | 1 | 2 | 3;
  rewards?: Array<{
    type: string;
    id: string;
    description?: string;
  }>;
}

interface PvEClientEvents {
  'pve:join': (data: { matchId: string }) => void;
  'pve:deploy': (data: {
    matchId: string;
    placements: Array<{ creatureId: string; position: { q: number; r: number } }>;
  }) => void;
  'pve:ready': (data: { matchId: string }) => void;
  'pve:leave': (data: { matchId: string }) => void;
}

interface PvEServerEvents {
  'pve:state': (state: PvEStatePayload) => void;
  'pve:combat-started': (data: { matchId: string }) => void;
  'pve:combat-state': (state: CombatState) => void;
  'pve:events': (events: CombatEvent[]) => void;
  'pve:completed': (result: PvEResultPayload) => void;
  'pve:error': (error: { code: string; message: string }) => void;
}

// =====================================================================
// SOCKET CLIENT
// =====================================================================

import { getApiUrl } from '../config/api';

/** @deprecated Use getApiUrl() from config/api */
function getSocketUrl(): string {
  return getApiUrl();
}

export class PvESocketClient {
  private socket: Socket<PvEServerEvents, PvEClientEvents> | null = null;
  private matchId: string | null = null;
  private joinPromise: Promise<PvEStatePayload | null> | null = null;
  private deltaDecoder: StateDeltaDecoder = new StateDeltaDecoder();

  /**
   * Ensure socket is connected (idempotent)
   */
  private ensureConnection(authToken?: string): void {
    if (this.socket?.connected) {
      return;
    }

    if (this.socket) {
      console.log('[PvE Socket] Existing socket disconnected, cleaning up...');
      // Full cleanup is OK here since we're destroying the socket entirely
      this.socket.removeAllListeners();
      this.socket.disconnect();
      this.socket = null;
      this.joinPromise = null;
    }

    const socketUrl = `${getSocketUrl()}/pve`;
    console.log('[PvE Socket] Creating socket connection to:', socketUrl);

    this.socket = io(socketUrl, {
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
      reconnectionAttempts: 5,
      autoConnect: false,
      auth: {
        token: authToken || ''
      }
    });

    this.socket.on('connect', () => {
      console.log('[PvE Socket] ✅ CONNECTED! Socket ID:', this.socket?.id);
      if (this.matchId) {
        this.joinMatch(this.matchId);
      }
    });

    this.socket.on('disconnect', (reason) => {
      console.log('[PvE Socket] ❌ DISCONNECTED. Reason:', reason);
    });

    this.socket.on('connect_error', (error) => {
      console.error('[PvE Socket] ❌ CONNECTION ERROR:', error.message);
    });

    this.socket.io.on('reconnect_attempt', () => {
      console.log('[PvE Socket] 🔄 Attempting reconnect...');
    });

    console.log('[PvE Socket] Initiating connection...');
    this.socket.connect();
  }

  /**
   * Connect to PvE socket and join match
   */
  async connect(matchId: string): Promise<PvEStatePayload | null> {
    // If switching to a different match, fully disconnect first to avoid stale state
    if (this.matchId && this.matchId !== matchId) {
      console.log('[PvE Socket] Switching match from', this.matchId, 'to', matchId, '- disconnecting old session');
      this.disconnect();
    }

    this.matchId = matchId;

    if (this.joinPromise) {
      return this.joinPromise;
    }

    let authToken: string | undefined;
    try {
      const auth = getAuth();
      const user = auth.currentUser;
      if (user) {
        authToken = await user.getIdToken();
        console.log('[PvE Socket] Got Firebase ID token for user:', user.uid);
      }
    } catch (error) {
      console.error('[PvE Socket] Failed to get auth token:', error);
    }

    this.joinPromise = new Promise<PvEStatePayload | null>((resolve, reject) => {
      this.ensureConnection(authToken);

      if (!this.socket) {
        reject(new Error('Failed to create socket'));
        return;
      }

      const timeout = setTimeout(() => {
        this.socket?.off('pve:state', onState);
        this.socket?.off('pve:error', onError);
        reject(new Error('Connection timeout'));
      }, 10000);

      const onState = (state: PvEStatePayload) => {
        clearTimeout(timeout);
        this.socket?.off('pve:state', onState);
        this.socket?.off('pve:error', onError);
        console.log('[PvE Socket] Successfully joined match');
        resolve(state);
      };

      const onError = (error: { code: string; message: string }) => {
        clearTimeout(timeout);
        this.socket?.off('pve:state', onState);
        this.socket?.off('pve:error', onError);
        console.error('[PvE Socket] Join failed:', error.message);
        reject(new Error(error.message));
      };

      this.socket.once('pve:state', onState);
      this.socket.once('pve:error', onError);

      if (this.socket.connected) {
        this.joinMatch(matchId);
      }
    });

    return this.joinPromise;
  }

  /**
   * Disconnect from PvE socket
   */
  disconnect(): void {
    if (this.socket) {
      if (this.matchId) {
        this.socket.emit('pve:leave', { matchId: this.matchId });
      }
      this.socket.disconnect();
      this.socket = null;
      this.matchId = null;
      this.joinPromise = null;
      this.deltaDecoder.reset();
      console.log('[PvE Socket] Disconnected');
    }
  }

  /**
   * Join a PvE match
   */
  private joinMatch(matchId: string): void {
    if (!this.socket) {
      console.error('[PvE Socket] Not connected');
      return;
    }
    this.socket.emit('pve:join', { matchId });
    console.log('[PvE Socket] Joining match:', matchId);
  }

  /**
   * Submit deployment and mark ready
   */
  deployAndReady(placements: Array<{ creatureId: string; position: { q: number; r: number } }>): void {
    if (!this.socket || !this.matchId) {
      console.error('[PvE Socket] Not connected or not joined');
      return;
    }

    console.log('[PvE Socket] Submitting deployment with', placements.length, 'creatures');
    this.socket.emit('pve:deploy', { matchId: this.matchId, placements });
    this.socket.emit('pve:ready', { matchId: this.matchId });
  }

  /**
   * Mark ready (triggers combat start for PvE)
   */
  markReady(): void {
    if (!this.socket || !this.matchId) {
      console.error('[PvE Socket] Not connected or not joined');
      return;
    }
    console.log('[PvE Socket] Marking ready for match:', this.matchId);
    this.socket.emit('pve:ready', { matchId: this.matchId });
  }

  // =====================================================================
  // EVENT LISTENERS
  // =====================================================================

  /**
   * Listen for state updates
   */
  onState(callback: (state: PvEStatePayload) => void): void {
    this.socket?.on('pve:state', callback);
  }

  /**
   * Listen for combat started
   */
  onCombatStarted(callback: (data: { matchId: string }) => void): void {
    this.socket?.on('pve:combat-started', callback);
  }

  /**
   * Listen for combat state updates
   * I1: Decodes delta-encoded broadcasts into full CombatState
   */
  onCombatState(callback: (state: CombatState) => void): void {
    this.socket?.on('pve:combat-state', (broadcast: CombatBroadcast | CombatState) => {
      let state: CombatState | null;
      if (broadcast && typeof broadcast === 'object' && 'type' in broadcast) {
        state = this.deltaDecoder.apply(broadcast as CombatBroadcast);
      } else {
        state = broadcast as CombatState;
      }
      if (state) {
        callback(state);
      }
    });
  }

  /**
   * Listen for combat events (damage_dealt, ability_used, etc.)
   * Enables damage numbers, health bars, and ability effects
   */
  onCombatEvents(callback: (events: CombatEvent[]) => void): void {
    this.socket?.on('pve:events', callback);
  }

  /**
   * Listen for combat completion
   */
  onCompleted(callback: (result: PvEResultPayload) => void): void {
    this.socket?.on('pve:completed', callback);
  }

  /**
   * Listen for errors
   */
  onError(callback: (error: { code: string; message: string }) => void): void {
    this.socket?.on('pve:error', callback);
  }

  /**
   * Remove all application-level event listeners.
   * Uses specific off() calls to avoid removing internal Socket.IO handlers
   * (connect, disconnect, connect_error) which are needed for reconnection.
   */
  removeAllListeners(): void {
    if (!this.socket) return;
    this.socket.off('pve:state');
    this.socket.off('pve:combat-started');
    this.socket.off('pve:combat-state');
    this.socket.off('pve:events');
    this.socket.off('pve:completed');
    this.socket.off('pve:error');
  }

  /**
   * Clear listeners for a specific event
   * Use this before re-registering to prevent accumulation
   */
  clearListeners(event: keyof PvEServerEvents): void {
    this.socket?.off(event);
  }

  /**
   * Clear all combat-related listeners without affecting connection handlers
   * Call this before registering new listeners to prevent duplicates
   */
  clearCombatListeners(): void {
    this.socket?.off('pve:combat-started');
    this.socket?.off('pve:combat-state');
    this.socket?.off('pve:events');
    this.socket?.off('pve:completed');
    this.socket?.off('pve:error');
    this.socket?.off('pve:state');
    console.log('[PvE Socket] Cleared combat listeners');
  }

  /**
   * Check if connected
   */
  get isConnected(): boolean {
    return this.socket?.connected || false;
  }

  /**
   * Get current match ID
   */
  get currentMatchId(): string | null {
    return this.matchId;
  }
}

/**
 * Singleton instance
 */
export const pveSocket = new PvESocketClient();
